import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import NormativeModelingView from '../../NormativeModelingView';
import { apiFetch } from '../../api';
import { formatFeatureLabel, getFeatureDefinition } from '../../featureLabels';
import { NORMATIVE_TABS } from '../constants';
import { usePatientZScores } from '../context/PatientZScoresContext';
import { useNormativeModels } from '../hooks/useNormativeData';
import { DistributionPlot } from './DistributionPlot';
import { FeatureCardModal } from './FeatureCardModal';
import { PatientNormativeResults } from './PatientNormativeResults';
import { SegmentedControl } from './SegmentedControl';


// Results component for normative models
export const NormativeResults = () => {
    const resultsRef = useRef(null);
    const [selectedRegions] = useState([]);
    const [expandedRegions, setExpandedRegions] = useState({});
    const [selectedModel, setSelectedModel] = useState(null);
    const { patientZScores, montageType, zScoreVisualizationSettings } = usePatientZScores();
    const selectedFeatures = useMemo(() => {
        return [zScoreVisualizationSettings?.feature || 'bp_gamma'];
    }, [zScoreVisualizationSettings?.feature]);
    const { models, loading, error } = useNormativeModels(selectedFeatures, selectedRegions, montageType);

    // Add state for active tab
    const [activeTab, setActiveTab] = useState(NORMATIVE_TABS.POPULATION);

    const handleTabChange = (value) => {
        setActiveTab(value);
    };

    // Debug patient z-scores data
    useEffect(() => {
        console.log("NormativeResults received patientZScores from context:", patientZScores);
    }, [patientZScores]);

    // Add AnimationLoader component
    // This is a helper component to inject the CSS animation
    const AnimationLoader = () => {
        useEffect(() => {
            const fadeInKeyframes = `
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translate(-50%, -50%) scale(0.8);
                    }
                    to {
                        opacity: 1;
                        transform: translate(-50%, -50%) scale(1);
                    }
                }
            `;

            const styleElement = document.createElement('style');
            styleElement.type = 'text/css';
            styleElement.appendChild(document.createTextNode(fadeInKeyframes));
            document.head.appendChild(styleElement);

            return () => {
                if (document.head.contains(styleElement)) {
                    document.head.removeChild(styleElement);
                }
            };
        }, []);

        return null; // This component doesn't render anything
    };

    // Listen for tab change events from NormativePanel
    useEffect(() => {
        const handleTabChange = (event) => {
            if (event.detail && event.detail.tab) {
                setActiveTab(event.detail.tab);
            }
        };

        document.addEventListener('normativeTabChange', handleTabChange);
        return () => {
            document.removeEventListener('normativeTabChange', handleTabChange);
        };
    }, []);

    const [regionDisplayMode, setRegionDisplayMode] = useState({});
    const regionDisplayModeRef = useRef({});
    useEffect(() => {
        regionDisplayModeRef.current = regionDisplayMode;
    }, [regionDisplayMode]);

    const [currentModalShowZScores, setCurrentModalShowZScores] = useState(true);
    const [currentModalGroupKey, setCurrentModalGroupKey] = useState(null);

    const [allChannelValues, setAllChannelValues] = useState({});
    const [channelValuesLoading, setChannelValuesLoading] = useState({});
    const [allZScores, setAllZScores] = useState({});
    const [zScoresLoading, setZScoresLoading] = useState({});

    useEffect(() => {
        setAllChannelValues({});
        setChannelValuesLoading({});
        setAllZScores({});
        setZScoresLoading({});
    }, [montageType]);

    const groupedModels = useMemo(() => {
        const isGroupByRegion = selectedFeatures.length > 0 || selectedRegions.length === 0;
        return models.reduce((acc, model) => {
            const key = isGroupByRegion ? model.region_name : model.feature_name;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(model);
            return acc;
        }, {});
    }, [models, selectedFeatures, selectedRegions]);

    useEffect(() => {
        if (Object.keys(groupedModels).length > 0) {
            const initialModes = {};
            let needsUpdate = false;

            Object.keys(groupedModels).forEach(groupKey => {
                if (regionDisplayModeRef.current[groupKey] === undefined) {
                    initialModes[groupKey] = true;
                    needsUpdate = true;
                }
            });

            if (needsUpdate) {
                setRegionDisplayMode(prevModes => ({ ...prevModes, ...initialModes }));
            }
        }
    }, [groupedModels]);

    const fetchRegionZScores = useCallback(async (groupKey) => {
        if (zScoresLoading[groupKey] || (allZScores[groupKey] && Object.values(allZScores[groupKey]).some(values => Array.isArray(values) && values.length > 0))) return;
        const currentGroupModels = groupedModels[groupKey] || [];
        if (currentGroupModels.length === 0) return;
        setZScoresLoading(prev => ({ ...prev, [groupKey]: true }));
        const newZScoresForGroup = {};
        const fetchPromises = currentGroupModels.map(async (model) => {
            try {
                const response = await apiFetch(
                    `/api/z-scores?region=${encodeURIComponent(model.region_name)}&feature=${encodeURIComponent(model.feature_name)}&montage=${encodeURIComponent(montageType)}`
                );

                // Check if response is not JSON
                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    console.error(`Non-JSON response when fetching z-scores for ${model.region_name}/${model.feature_name}`);
                    newZScoresForGroup[model.feature_name] = [];
                    return;
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                newZScoresForGroup[model.feature_name] = data;
            } catch (err) {
                console.error(`Error fetching z-scores for ${model.region_name}/${model.feature_name}:`, err);
                newZScoresForGroup[model.feature_name] = [];
            }
        });
        await Promise.all(fetchPromises);
        setAllZScores(prev => ({ ...prev, [groupKey]: newZScoresForGroup }));
        setZScoresLoading(prev => ({ ...prev, [groupKey]: false }));
    }, [groupedModels, allZScores, zScoresLoading, montageType]);

    const fetchRegionChannelValues = useCallback(async (groupKey) => {
        if (channelValuesLoading[groupKey] || (allChannelValues[groupKey] && Object.values(allChannelValues[groupKey]).some(values => Array.isArray(values) && values.length > 0))) return;
        const currentGroupModels = groupedModels[groupKey] || [];
        if (currentGroupModels.length === 0) return;
        setChannelValuesLoading(prev => ({ ...prev, [groupKey]: true }));
        const newChannelValuesForGroup = {};
        const fetchPromises = currentGroupModels.map(async (model) => {
            try {
                const response = await apiFetch(
                    `/api/channel-values?region=${encodeURIComponent(model.region_name)}&feature=${encodeURIComponent(model.feature_name)}&montage=${encodeURIComponent(montageType)}`
                );

                // Check if response is not JSON
                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    console.error(`Non-JSON response when fetching channel values for ${model.region_name}/${model.feature_name}`);
                    newChannelValuesForGroup[model.feature_name] = [];
                    return;
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                newChannelValuesForGroup[model.feature_name] = data;
            } catch (err) {
                console.error(`Error fetching channel values for ${model.region_name}/${model.feature_name}:`, err);
                newChannelValuesForGroup[model.feature_name] = [];
            }
        });
        await Promise.all(fetchPromises);
        setAllChannelValues(prev => ({ ...prev, [groupKey]: newChannelValuesForGroup }));
        setChannelValuesLoading(prev => ({ ...prev, [groupKey]: false }));
    }, [groupedModels, allChannelValues, channelValuesLoading, montageType]);

    const toggleRegion = useCallback((groupName) => {
        setExpandedRegions(prevExpanded => {
            const isCurrentlyExpanded = !!prevExpanded[groupName];
            const newExpandedState = { ...prevExpanded, [groupName]: !isCurrentlyExpanded };
            if (!isCurrentlyExpanded) {
                fetchRegionChannelValues(groupName);
                fetchRegionZScores(groupName);
                setRegionDisplayMode(prevModes => {
                    if (prevModes[groupName] === undefined) {
                        return { ...prevModes, [groupName]: true };
                    }
                    return prevModes;
                });
            }
            return newExpandedState;
        });
    }, [fetchRegionChannelValues, fetchRegionZScores]);

    const getChannelValuesForModel = (model, groupKey) => {
        if (!groupKey || !allChannelValues[groupKey] || !allChannelValues[groupKey][model.feature_name]) {
            return [];
        }
        return allChannelValues[groupKey][model.feature_name] || [];
    };

    const getZScoresForModel = (model, groupKey) => {
        if (!groupKey || !allZScores[groupKey] || !allZScores[groupKey][model.feature_name]) {
            return [];
        }
        return allZScores[groupKey][model.feature_name] || [];
    };

    const openFeatureModal = useCallback((modelToOpen, showZForModal, groupKeyForModal) => {
        setSelectedModel(modelToOpen);
        setCurrentModalShowZScores(showZForModal);
        setCurrentModalGroupKey(groupKeyForModal);
    }, []);

    const closeFeatureModal = useCallback(() => {
        setSelectedModel(null);
        setCurrentModalGroupKey(null);
    }, []);

    useEffect(() => {
        if (resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    if (loading) {
        return (
            <div className="normative-results-container" ref={resultsRef}>
                <h2>Normative Modeling</h2>
                <div className="loading-message">Loading normative data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="normative-results-container" ref={resultsRef}>
                <h2>Normative Modeling</h2>
                <div className="normative-error">
                    {error.includes("<!doctype") || error.includes("Unexpected token '<'") ?
                        "No normative data available." :
                        `Error: ${error}`}
                </div>
            </div>
        );
    }

    if (models.length === 0 && activeTab === NORMATIVE_TABS.POPULATION) {
        return (
            <div className="normative-results-container" ref={resultsRef}>
                <h2>Normative Modeling</h2>
                <div className="normative-tabs">
                    <SegmentedControl
                        options={[
                            { label: 'Population Normative Modeling', value: NORMATIVE_TABS.POPULATION },
                            { label: 'Patient Normative Modeling', value: NORMATIVE_TABS.PATIENT },
                            { label: 'Population Distribution', value: NORMATIVE_TABS.POPULATION_DISTRIBUTION }
                        ]}
                        selectedValue={activeTab}
                        onChange={handleTabChange}
                        name="normative-results-tabs-empty"
                    />
                </div>
                <p>No population normative model is available for the selected z-score feature.</p>
            </div>
        );
    }

    return (
        <div className="normative-results-container" ref={resultsRef}>
            <AnimationLoader />
            <div className="normative-results-header">
                <h2>Normative Modeling</h2>
            </div>

            <div className="normative-tabs">
                <SegmentedControl
                    options={[
                        { label: 'Population Normative Modeling', value: NORMATIVE_TABS.POPULATION },
                        { label: 'Patient Normative Modeling', value: NORMATIVE_TABS.PATIENT },
                        { label: 'Population Distribution', value: NORMATIVE_TABS.POPULATION_DISTRIBUTION }
                    ]}
                    selectedValue={activeTab}
                    onChange={handleTabChange}
                    name="normative-results-tabs"
                />
            </div>

            {activeTab === NORMATIVE_TABS.POPULATION && (
                <div className="normative-results-content">
                    {Object.entries(groupedModels).map(([groupName, groupModels]) => {
                        const currentShowZScores = regionDisplayMode[groupName] === undefined ? true : regionDisplayMode[groupName];
                        const maxChannelCount = groupModels.reduce((max, model) => {
                            const count = (currentShowZScores ? getZScoresForModel(model, groupName).length : getChannelValuesForModel(model, groupName).length) || model.sample_size;
                            return Math.max(max, count);
                        }, 0);

                        return (
                            <div key={groupName} className="normative-group-section">
                                <div className="region-header" onClick={() => toggleRegion(groupName)}>
                                    <h3>
                                        <span className="toggle-icon">
                                            {expandedRegions[groupName] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                        </span>
                                        {groupName}
                                        <span className="feature-count">({groupModels.length} {groupModels.length === 1 ? 'feature' : 'features'}, {maxChannelCount} {maxChannelCount === 1 ? 'channel' : 'channels'})</span>
                                    </h3>
                                </div>
                                {expandedRegions[groupName] && (
                                    <>
                                        <div className="region-controls" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                            <SegmentedControl
                                                name={`displayMode-${groupName}`}
                                                options={[
                                                    { label: 'Z-Scores', value: true },
                                                    { label: 'Raw Values', value: false }
                                                ]}
                                                selectedValue={currentShowZScores}
                                                onChange={(value) => setRegionDisplayMode(prev => ({ ...prev, [groupName]: value }))}
                                            />
                                        </div>
                                        <div className="normative-feature-grid">
                                            {groupModels.map(model => {
                                                const channelValues = getChannelValuesForModel(model, groupName);
                                                const isLoading = channelValuesLoading[groupName] || zScoresLoading[groupName];
                                                const zScores = getZScoresForModel(model, groupName);
                                                return (
                                                    <div
                                                        key={`${model.region_name}-${model.feature_name}`}
                                                        className="normative-feature-card"
                                                        onClick={() => openFeatureModal(model, currentShowZScores, groupName)}
                                                    >
                                                        <h4
                                                            className="normative-feature-heading"
                                                            title={model.feature_name === groupName ? model.region_name : getFeatureDefinition(model.feature_name).description}
                                                        >
                                                            <span className="feature-card-title-boxed">
                                                                {model.feature_name === groupName ? model.region_name : formatFeatureLabel(model.feature_name)}
                                                            </span>
                                                        </h4>
                                                        <div className="normative-stats">
                                                            <div className="stat">
                                                                <span className="stat-label">Mean:</span>
                                                                <span className="stat-value">
                                                                    {model.mean.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            <div className="stat">
                                                                <span className="stat-label">Std:</span>
                                                                <span className="stat-value">
                                                                    {model.std.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="normative-stats">
                                                            <div className="stat">
                                                                <span className="stat-label">Min:</span>
                                                                <span className="stat-value">
                                                                    {model.min.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            <div className="stat">
                                                                <span className="stat-label">Max:</span>
                                                                <span className="stat-value">
                                                                    {model.max.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="normative-stats">
                                                            <div className="stat">
                                                                <span className="stat-label">Channels:</span>
                                                                <span className="stat-value">
                                                                    {(currentShowZScores ? zScores.length : channelValues.length) || model.sample_size}
                                                                </span>
                                                            </div>
                                                            <div className="stat">
                                                                <span className="stat-label">Display:</span>
                                                                <span className="stat-value z-score-value">
                                                                    {currentShowZScores ? "Z-Scores" : "Raw Values"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <DistributionPlot
                                                            model={model}
                                                            channelValues={channelValues}
                                                            channelValuesLoading={isLoading}
                                                            zScores={zScores}
                                                            zScoresLoading={isLoading}
                                                            showZScores={currentShowZScores}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {activeTab === NORMATIVE_TABS.PATIENT && (
                <div className="normative-patient-results">
                    {patientZScores.length > 0 ? (
                        <PatientNormativeResults patientZScores={patientZScores} />
                    ) : (
                        <div className="placeholder-message">
                            Patient-level normative modeling results will appear here after selecting patients and features and generating z-scores.
                            <br />
                            In IEEG Abnormalities, select at least one patient and one z-score feature, then click "Plot Z-Score Points".
                        </div>
                    )}
                </div>
            )}

            {activeTab === NORMATIVE_TABS.POPULATION_DISTRIBUTION && (
                <div className="population-distribution-wrapper">
                    <NormativeModelingView />
                </div>
            )}

            {selectedModel && currentModalGroupKey && (
                <FeatureCardModal
                    isOpen={!!selectedModel}
                    onClose={closeFeatureModal}
                    model={selectedModel}
                    channelValues={getChannelValuesForModel(selectedModel, currentModalGroupKey)}
                    channelValuesLoading={channelValuesLoading[currentModalGroupKey] || zScoresLoading[currentModalGroupKey]}
                    zScores={getZScoresForModel(selectedModel, currentModalGroupKey)}
                    zScoresLoading={zScoresLoading[currentModalGroupKey] || channelValuesLoading[currentModalGroupKey]}
                    showZScores={currentModalShowZScores}
                />
            )}
        </div>
    );
};
